import { useEffect, useLayoutEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function ProtectedRoute({ children, requireAdmin = false }) {
  const { currentUser, userRole, logout, validateSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Immediate check before rendering
  useLayoutEffect(() => {
    // If user tries to go back to login while having a session, log them out
    if (location.pathname === '/login' && currentUser) {
      handleInvalidSession();
      return;
    }

    // Only check session if we're not already on the dashboard
    if (location.pathname !== '/dashboard' && (!currentUser || !validateSession())) {
      handleInvalidSession();
    }
  }, [location.pathname]);

  useEffect(() => {
    const checkSession = () => {
      // If user navigates to login while having a session, log them out
      if (location.pathname === '/login' && currentUser) {
        handleInvalidSession();
        return false;
      }

      // Only check session if we're not already on the dashboard
      if (location.pathname !== '/dashboard' && (!currentUser || !validateSession())) {
        handleInvalidSession();
        return false;
      }
      return true;
    };

    // Check session on mount
    if (!checkSession()) return;

    // Set up periodic check
    const sessionCheckInterval = setInterval(checkSession, 30000);

    // Handle visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };

    // Handle all navigation attempts
    const handleNavigation = () => {
      // Force logout if navigating to login page
      if (location.pathname === '/login') {
        handleInvalidSession();
        return;
      }

      if (!checkSession()) {
        // Prevent default navigation and redirect
        window.history.replaceState(null, '', '/login');
        navigate('/login', { replace: true });
      }
    };

    // Handle forward/back navigation
    window.onpopstate = handleNavigation;
    window.onpushstate = handleNavigation;

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('popstate', handleNavigation);

    return () => {
      clearInterval(sessionCheckInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('popstate', handleNavigation);
      window.onpopstate = null;
      window.onpushstate = null;
    };
  }, [currentUser, validateSession, navigate, location]);

  const handleInvalidSession = () => {
    // Clear any existing history state
    window.history.replaceState(null, '', '/login');
    
    // Ensure we're logged out
    logout();
    
    // Navigate to login, replacing the current history entry
    navigate('/login', { replace: true });
  };

  // Check admin requirement
  if (requireAdmin && userRole !== 'admin') {
    navigate('/', { replace: true });
    return null;
  }

  // If user tries to access login page while having a session, log them out
  if (location.pathname === '/login' && currentUser) {
    handleInvalidSession();
    return null;
  }

  // Block rendering if no valid session
  if (!currentUser || !validateSession()) {
    return null;
  }

  return children;
} 